import { FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

import { NoDataMessage } from "src/components/NoDataMessage";
import AircraftOverviewTooltip from "src/components/AircraftOverviewTooltip";
import { overviewTableMaxHeightSx } from "src/components/overviewTableStyles";
import type { AircraftOverviewColumn, AircraftOverviewRow } from "src/shared/api-types/certificate";

interface AircraftOverviewTableProps {
  columns: AircraftOverviewColumn[];
  rows: AircraftOverviewRow[];
  onClearFilters?: () => void;
  // Clicking a status opens its course details, anchored at the status
  onOpenDetails: (cell: { anchor: HTMLElement; row: AircraftOverviewRow; column: AircraftOverviewColumn }) => void;
}

const StickyCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: "white",
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  padding: "6px 8px",
  position: "sticky",
  whiteSpace: "nowrap",
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    boxShadow: `inset -1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 ${theme.palette.divider}`,
  },
  "&.MuiTableCell-head": {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 600,
    top: 0,
    zIndex: theme.zIndex.appBar - 1,
  },
}));

const UserCell = styled(StickyCell)({
  left: 0,
});

const AircraftCell = styled(StickyCell)({
  left: 0,
});

const OperationHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  fontWeight: 600,
  height: 132,
  minWidth: 52,
  padding: "6px 4px",
  position: "sticky",
  top: 0,
  verticalAlign: "bottom",
  zIndex: theme.zIndex.appBar - 3,
  [theme.breakpoints.down("md")]: {
    boxShadow: `inset -1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 ${theme.palette.divider}`,
  },
}));

const StatusCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: "white",
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  minWidth: 52,
  padding: "6px 4px",
  textAlign: "center",
  [theme.breakpoints.down("md")]: {
    boxShadow: `inset -1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 ${theme.palette.divider}`,
  },
}));

const tableSx = {
  borderLeft: "1px solid #e0e0e0",
  borderTop: "1px solid #e0e0e0",
  borderCollapse: "separate",
  borderSpacing: 0,
  "& .MuiTableRow-root": {
    backgroundColor: "white",
  },
};

const tableContainerSx = {
  ...overviewTableMaxHeightSx,
  overflow: "auto",
  pb: 1,
};

// A user's aircraft rows come one after another; each group shows the user name once (also in the Excel export)
export const groupRowsByUser = (rows: AircraftOverviewRow[]) => {
  const groups: Array<{ userId: number; userName: string; rows: AircraftOverviewRow[] }> = [];
  rows.forEach((row) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.userId === row.userId) {
      lastGroup.rows.push(row);
    } else {
      groups.push({ userId: row.userId, userName: row.userName, rows: [row] });
    }
  });
  return groups;
};

const AircraftOverviewTable: FC<AircraftOverviewTableProps> = ({ columns, rows, onClearFilters, onOpenDetails }) => {
  const { t } = useTranslation();
  const userHeaderRef = useRef<HTMLTableCellElement | null>(null);
  const [userColumnWidth, setUserColumnWidth] = useState(0);

  const groupedRows = useMemo(() => groupRowsByUser(rows), [rows]);

  useLayoutEffect(() => {
    const updateWidth = () => setUserColumnWidth(userHeaderRef.current?.offsetWidth ?? 0);
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [rows.length]);

  if (columns.length === 0 || rows.length === 0) {
    return <NoDataMessage minHeight="300px" onClearFilters={onClearFilters} />;
  }

  return (
    <TableContainer sx={tableContainerSx}>
      <Table stickyHeader size="small" sx={tableSx}>
        <TableHead>
          <TableRow>
            <UserCell ref={userHeaderRef}>{`${t("user")} (${groupedRows.length})`}</UserCell>
            <AircraftCell sx={{ left: userColumnWidth }}>{t("compliance.aircraft")}</AircraftCell>
            {columns.map((column) => (
              <OperationHeaderCell key={column.key} align="center">
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    height: 112,
                    justifyContent: "center",
                    mx: "auto",
                    transform: "rotate(-90deg)",
                    transformOrigin: "center",
                    whiteSpace: "normal",
                    width: 112,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      display: "block",
                      lineHeight: 1.2,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {column.operationName}
                  </Typography>
                </Box>
              </OperationHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groupedRows.map((group) =>
            group.rows.map((row, rowIndex) => (
              <TableRow key={row.key}>
                {rowIndex === 0 && (
                  <UserCell component="th" scope="row" rowSpan={group.rows.length}>
                    {group.userName}
                  </UserCell>
                )}
                <AircraftCell sx={{ left: userColumnWidth }}>{row.aircraftName}</AircraftCell>
                {columns.map((column) => {
                  const operationCompliance = row.operationCompliance[column.key];
                  return (
                    <StatusCell key={column.key}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {operationCompliance ? (
                          <AircraftOverviewTooltip
                            compliance={operationCompliance}
                            operationName={column.operationName}
                            aircraftName={row.aircraftName}
                            onOpenDetails={(anchor) => onOpenDetails({ anchor, row, column })}
                          />
                        ) : (
                          <Box sx={{ width: 28, height: 28 }} />
                        )}
                      </Box>
                    </StatusCell>
                  );
                })}
              </TableRow>
            )),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AircraftOverviewTable;
